import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  HardDrive, 
  Folder, 
  MapPin, 
  MapPinOff, 
  Search,
  Wifi,
  WifiOff,
  Settings,
  File,
  ArrowLeft,
  Eye,
  Upload,
  Image
} from 'lucide-react';
import { nasService } from '../services/nasService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const NAS = () => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentPath, setCurrentPath] = useState('/');
  const [showBrowser, setShowBrowser] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('/');
  const [showMapAccessModal, setShowMapAccessModal] = useState(false);
  const [selectedFolderPath, setSelectedFolderPath] = useState('');
  const queryClient = useQueryClient();

  const { data: nasDevices, isLoading: devicesLoading } = useQuery(
    'nas-devices',
    nasService.getNASDevices
  );

  const { data: mappedDrives, isLoading: mappedLoading } = useQuery(
    'mapped-drives',
    nasService.getMappedDrives
  );

  const { data: folders, isLoading: foldersLoading } = useQuery(
    ['nas-folders', selectedDevice?._id],
    () => nasService.getFolders(selectedDevice._id),
    { enabled: !!selectedDevice }
  );

  const { data: folderContents, isLoading: contentsLoading } = useQuery(
    ['nas-browse', selectedDevice?._id, currentPath],
    () => nasService.browseFolder(selectedDevice._id, currentPath),
    { enabled: !!selectedDevice && showBrowser }
  );

  const scanFoldersMutation = useMutation(
    (deviceId) => nasService.scanFolders(deviceId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['nas-folders', selectedDevice?._id]);
        toast.success('Skanowanie folderów zakończone');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas skanowania folderów');
      }
    }
  );

  const mapDriveMutation = useMutation(
    ({ deviceId, folderId, driveLetter }) => 
      nasService.mapDrive(deviceId, folderId, driveLetter),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mapped-drives');
        queryClient.invalidateQueries(['nas-folders', selectedDevice?._id]);
        setShowMapModal(false);
        toast.success('Dysk został zmapowany');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas mapowania dysku');
      }
    }
  );

  const unmapDriveMutation = useMutation(
    ({ deviceId, folderId }) => nasService.unmapDrive(deviceId, folderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mapped-drives');
        queryClient.invalidateQueries(['nas-folders', selectedDevice?._id]);
        toast.success('Dysk został odmapowany');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas odmapowania dysku');
      }
    }
  );

  const uploadFileMutation = useMutation(
    ({ deviceId, folderPath, fileName, fileData }) => 
      nasService.uploadFile(deviceId, folderPath, fileName, fileData),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Plik został pomyślnie wysłany');
        setShowUploadModal(false);
        setSelectedFiles([]);
        queryClient.invalidateQueries(['nas-browse', selectedDevice?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas wysyłania pliku');
      }
    }
  );

  const mapFolderAccessMutation = useMutation(
    ({ deviceId, folderPath, permissions }) => 
      nasService.assignFolderAccess(deviceId, folderPath, permissions),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Dostęp do folderu został przypisany');
        setShowMapAccessModal(false);
        setSelectedFolderPath('');
        queryClient.invalidateQueries(['folder-access', selectedDevice?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas przypisywania dostępu do folderu');
      }
    }
  );

  const handleScanFolders = (deviceId) => {
    scanFoldersMutation.mutate(deviceId);
  };

  const handleBrowseFolder = (deviceId, folderPath = '/') => {
    setSelectedDevice(nasDevices?.devices?.find(d => d._id === deviceId));
    setCurrentPath(folderPath);
    setShowBrowser(true);
  };

  const handleNavigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  const handleGoBack = () => {
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    setCurrentPath(parentPath);
  };

  const handleMapFolderAccess = (folderPath) => {
    if (!selectedDevice) {
      toast.error('Nie wybrano urządzenia NAS');
      return;
    }

    // Pokaż modal do mapowania dostępu
    setShowMapAccessModal(true);
    setSelectedFolderPath(folderPath);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'docx': return '📝';
      case 'txt': return '📄';
      case 'lnk': return '🔗';
      case 'jpg': case 'png': case 'gif': return '🖼️';
      case 'mp4': case 'avi': return '🎥';
      case 'mp3': case 'wav': return '🎵';
      case 'zip': case 'rar': return '📦';
      case 'exe': return '⚙️';
      default: return '📄';
    }
  };

  const handleMapDrive = (folder) => {
    setSelectedFolder(folder);
    setShowMapModal(true);
  };

  const handleUnmapDrive = (deviceId, folderId) => {
    if (window.confirm('Czy na pewno chcesz odmapować ten dysk?')) {
      unmapDriveMutation.mutate({ deviceId, folderId });
    }
  };

  const handleRemoveFolderAccess = (deviceId, folderPath) => {
    if (window.confirm('Czy na pewno chcesz usunąć dostęp do tego folderu?')) {
      // TODO: Implement remove folder access
      console.log('Remove access to:', deviceId, folderPath);
    }
  };

  const handleMapFolder = (deviceId, folderId) => {
    // Znajdź folder w liście folderów
    const folder = folders?.folders?.find(f => f._id === folderId);
    if (folder) {
      setSelectedFolder(folder);
      setShowMapModal(true);
    }
  };

  const handleUnmapFolder = (deviceId, folderId) => {
    if (window.confirm('Czy na pewno chcesz odmapować ten folder?')) {
      unmapDriveMutation.mutate({ deviceId, folderId });
    }
  };

  const onSubmitMapDrive = (e) => {
    e.preventDefault();
    const driveLetter = e.target.driveLetter.value;
    if (selectedFolder && selectedDevice) {
      mapDriveMutation.mutate({
        deviceId: selectedDevice._id,
        folderId: selectedFolder._id,
        driveLetter
      });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUploadFiles = () => {
    if (!selectedDevice || selectedFiles.length === 0) return;

    selectedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = e.target.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        uploadFileMutation.mutate({
          deviceId: selectedDevice._id,
          folderPath: uploadFolder,
          fileName: file.name,
          fileData: fileData
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFolder(currentPath);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      online: 'status-online',
      offline: 'status-offline',
      unknown: 'bg-gray-100 text-gray-800'
    };

    const statusText = {
      online: 'Online',
      offline: 'Offline',
      unknown: 'Nieznany'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status]}`}>
        {status === 'online' ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
        {statusText[status]}
      </span>
    );
  };

  if (devicesLoading || mappedLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mapped Drives Overview */}
      {mappedDrives?.mappedDrives?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Zmapowane dyski
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {mappedDrives.mappedDrives.map((drive) => (
                <div key={`${drive.deviceId}-${drive.folderPath}`} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {drive.folderName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {drive.deviceName} ({drive.deviceIP})
                      </p>
                      <p className="text-xs text-gray-400">
                        {drive.folderPath}
                      </p>
                      <div className="flex space-x-2 mt-1">
                        {drive.permissions.read && <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">Odczyt</span>}
                        {drive.permissions.write && <span className="text-xs bg-green-100 text-green-800 px-1 rounded">Zapis</span>}
                        {drive.permissions.upload && <span className="text-xs bg-purple-100 text-purple-800 px-1 rounded">Upload</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleBrowseFolder(drive.deviceId, drive.folderPath)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Przeglądaj zawartość"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveFolderAccess(drive.deviceId, drive.folderPath)}
                      className="text-red-600 hover:text-red-800"
                      title="Usuń dostęp"
                    >
                      <MapPinOff className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Two Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - NAS Devices */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Urządzenia NAS
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {nasDevices?.devices?.map((device) => (
                  <div key={device._id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <HardDrive className="h-5 w-5 text-blue-600" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900">
                            {device.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {device.ipAddress}:{device.port}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(device.status)}
                    </div>

                    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                      <span>Foldery: {device.folders?.length || 0}</span>
                      <span>
                        {new Date(device.lastSeen).toLocaleDateString('pl-PL')}
                      </span>
                    </div>

                    <div className="mt-3 flex space-x-2">
                      <button
                        onClick={() => setSelectedDevice(device)}
                        className="flex-1 btn-secondary text-xs py-1"
                      >
                        <Folder className="h-3 w-3 mr-1" />
                        Foldery
                      </button>
                      <button
                        onClick={() => handleBrowseFolder(device._id, '/')}
                        className="flex-1 btn-primary text-xs py-1"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Przeglądaj
                      </button>
                      <button
                        onClick={() => handleScanFolders(device._id)}
                        disabled={scanFoldersMutation.isLoading}
                        className="flex-1 btn-secondary text-xs py-1"
                      >
                        {scanFoldersMutation.isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Search className="h-3 w-3 mr-1" />
                            Skanuj
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDevice(device);
                          openUploadModal();
                        }}
                        className="flex-1 btn-success text-xs py-1"
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Upload
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Folders Panel */}
          {selectedDevice && !showBrowser && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Foldery - {selectedDevice.name}
                  </h3>
                  <button
                    onClick={() => setSelectedDevice(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="card-body">
                {folders?.folders?.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3">
                    {folders.folders.map((folder) => (
                      <div key={folder._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <Folder className="h-4 w-4 text-blue-600 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {folder.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {folder.path}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleBrowseFolder(selectedDevice._id, folder.path)}
                            className="text-green-600 hover:text-green-800"
                            title="Przeglądaj folder"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {folder.isMapped ? (
                            <button
                              onClick={() => handleUnmapFolder(selectedDevice._id, folder._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Odmapuj folder"
                            >
                              <MapPinOff className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleMapFolder(selectedDevice._id, folder._id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Mapuj folder"
                            >
                              <MapPin className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Folder className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Brak folderów</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Kliknij "Skanuj" aby znaleźć foldery na NAS
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Browser */}
        <div className="space-y-6">
          {showBrowser && selectedDevice ? (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setShowBrowser(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Przeglądarka - {selectedDevice.name}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {currentPath}
                    </span>
                    {currentPath !== '/' && (
                      <button
                        onClick={handleGoBack}
                        className="text-gray-400 hover:text-gray-600"
                        title="Wróć do poprzedniego folderu"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleMapFolderAccess(currentPath)}
                      className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors"
                      title="Mapuj dostęp do tego folderu"
                    >
                      Mapuj dostęp
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body max-h-96 overflow-y-auto">
                {contentsLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : folderContents ? (
                  <div className="space-y-4">
                    {/* Folders */}
                    {folderContents.contents.folders.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Foldery ({folderContents.contents.folders.length})
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {folderContents.contents.folders.map((folder, index) => (
                            <div
                              key={index}
                              onClick={() => handleNavigateToFolder(folder.path)}
                              className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                            >
                              <Folder className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {folder.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(folder.lastModified).toLocaleDateString('pl-PL')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Files */}
                    {folderContents.contents.files.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Pliki ({folderContents.contents.files.length})
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {folderContents.contents.files.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <span className="text-lg mr-3">
                                {getFileIcon(file.type)}
                              </span>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.size)} • {new Date(file.lastModified).toLocaleDateString('pl-PL')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {folderContents.contents.folders.length === 0 && folderContents.contents.files.length === 0 && (
                      <div className="text-center py-8">
                        <Folder className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Folder jest pusty</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <LoadingSpinner />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <div className="text-center py-12">
                  <Eye className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Przeglądarka folderów
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Wybierz urządzenie NAS i kliknij "Przeglądaj" aby rozpocząć
                  </p>
                  <div className="text-sm text-gray-400">
                    <p>• Przeglądaj foldery i pliki</p>
                    <p>• Nawiguj przez strukturę katalogów</p>
                    <p>• Zobacz szczegóły plików</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Map Drive Modal */}
      {showMapModal && selectedFolder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Mapuj dysk
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Folder: <span className="font-medium">{selectedFolder.name}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Ścieżka: {selectedFolder.path}
                </p>
              </div>
              <form onSubmit={onSubmitMapDrive} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Litera dysku
                  </label>
                  <select
                    name="driveLetter"
                    required
                    className="input"
                  >
                    <option value="">Wybierz literę</option>
                    {Array.from({ length: 26 }, (_, i) => {
                      const letter = String.fromCharCode(65 + i);
                      return (
                        <option key={letter} value={letter}>
                          {letter}:
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowMapModal(false)}
                    className="flex-1 btn-secondary"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={mapDriveMutation.isLoading}
                    className="flex-1 btn-primary"
                  >
                    {mapDriveMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      'Mapuj dysk'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Wyślij pliki do NAS
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder docelowy
                </label>
                <input
                  type="text"
                  value={uploadFolder}
                  onChange={(e) => setUploadFolder(e.target.value)}
                  className="input"
                  placeholder="/elpro-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wybierz pliki
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="input"
                />
              </div>

              {selectedFiles.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Wybrane pliki:</p>
                  <div className="space-y-1">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-700">
                        <Image className="h-4 w-4 mr-2" />
                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleUploadFiles}
                  disabled={selectedFiles.length === 0 || uploadFileMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {uploadFileMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Wyślij pliki'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do mapowania dostępu do folderu */}
      {showMapAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Mapuj dostęp do folderu
              </h3>
              <button
                onClick={() => setShowMapAccessModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ścieżka folderu
                </label>
                <input
                  type="text"
                  value={selectedFolderPath}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uprawnienia
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="mr-2"
                    />
                    <span className="text-sm">Odczyt (read)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="mr-2"
                    />
                    <span className="text-sm">Zapis (write)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="mr-2"
                    />
                    <span className="text-sm">Upload plików (upload)</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowMapAccessModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  onClick={() => {
                    const permissions = {
                      read: true,
                      write: true,
                      upload: true
                    };
                    mapFolderAccessMutation.mutate({
                      deviceId: selectedDevice._id,
                      folderPath: selectedFolderPath,
                      permissions
                    });
                  }}
                  disabled={mapFolderAccessMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {mapFolderAccessMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Mapuj dostęp'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NAS;
